// /app/frontend/src/components/NotificationBell.jsx
//
// Header bell + dropdown notification center.
//
//   • Green dot lights up whenever unread_count > 0 (polled every 30s).
//   • Click bell → opens a right-aligned dropdown with newest-first list.
//   • Click a row → marks it read + navigates to its `link` when present.
//   • "Mark all as read" button at the top of the dropdown.
//
// Isolated by design — no globals, no context, no cross-page state. Adding
// a new notification kind (future Feed features) requires ZERO change here;
// the row is rendered generically from {title, body, actor, link, kind}.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../lib/notificationsApi";
import useNotificationsPolling from "../hooks/useNotificationsPolling";

const KIND_ICONS = {
  friend_request: "👥",
  friend_accepted: "🤝",
  direct_message: "💬",
  post_like: "❤️",
  post_comment: "💭",
  badge_unlock: "🏆",
  mock_test_improvement: "📈",
  targets_all_done: "🎯",
  general: "🔔",
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Math.max(0, Date.now() - d.getTime());
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// Extracts the initial for the actor avatar fallback. Kept as a pure fn so
// it can be trivially tested if we ever add unit tests here.
const initial = (name) => (name?.trim?.()?.[0] || "?").toUpperCase();

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const alive = useRef(true);

  const { unreadCount, refresh, decrement, setCount } = useNotificationsPolling();

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { items: rows, unreadCount: ct } = await fetchNotifications({ limit: 30 });
      if (!alive.current) return;
      setItems(rows);
      setCount(ct);
    } catch {
      /* silent — user retries by re-opening */
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [setCount]);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // Refresh the list every time the dropdown opens so the user sees a
  // fresh snapshot (the count is already fresh from the poll).
  useEffect(() => {
    if (open) loadList();
  }, [open, loadList]);

  const onRowClick = useCallback(
    async (row) => {
      // Optimistic UI — flip locally, fire request, revert on failure.
      const wasUnread = !row.read;
      if (wasUnread) {
        setItems((prev) =>
          prev.map((r) =>
            r.notification_id === row.notification_id ? { ...r, read: true } : r
          )
        );
        decrement(1);
        try {
          await markNotificationRead(row.notification_id);
        } catch {
          // Revert on failure. Refresh from server for authoritative count.
          setItems((prev) =>
            prev.map((r) =>
              r.notification_id === row.notification_id ? { ...r, read: false } : r
            )
          );
          refresh();
        }
      }
      setOpen(false);
      if (row.link) {
        // Support both plain paths and hash-fragment links (/profile#badges).
        navigate(row.link);
      }
    },
    [decrement, navigate, refresh]
  );

  const onMarkAllRead = useCallback(async () => {
    if (unreadCount === 0) return;
    setMarkingAll(true);
    // Optimistic
    setItems((prev) => prev.map((r) => ({ ...r, read: true })));
    setCount(0);
    try {
      await markAllNotificationsRead();
    } catch {
      refresh();
      loadList();
    } finally {
      setMarkingAll(false);
    }
  }, [unreadCount, setCount, refresh, loadList]);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid="notification-bell"
          aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ""}`}
          className="relative"
        >
          <Bell size={18} />
          {unreadCount > 0 && (
            <span
              data-testid="notification-bell-dot"
              className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-zinc-950"
            />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-[360px] p-0 sm:w-[380px]"
        data-testid="notification-dropdown"
      >
        <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
          <div>
            <div className="font-heading text-sm font-semibold">Notifications</div>
            <div className="text-xs text-zinc-500" data-testid="notification-unread-summary">
              {unreadCount === 0 ? "You're all caught up" : `${unreadCount} unread`}
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={onMarkAllRead}
              disabled={markingAll}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-emerald-600 hover:bg-emerald-500/10 disabled:opacity-50 dark:text-emerald-400"
              data-testid="notification-mark-all-read"
            >
              {markingAll ? <Loader2 size={12} className="animate-spin" /> : <CheckCheck size={12} />}
              Mark all read
            </button>
          )}
        </div>

        <div className="max-h-[420px] overflow-y-auto" data-testid="notification-list">
          {loading && items.length === 0 && (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-zinc-500">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          )}

          {!loading && items.length === 0 && (
            <div
              className="flex flex-col items-center gap-2 py-12 text-center text-sm text-zinc-500"
              data-testid="notification-empty"
            >
              <div className="grid h-11 w-11 place-items-center rounded-full bg-zinc-100 text-zinc-400 dark:bg-zinc-900">
                <Bell size={18} />
              </div>
              <div>No notifications yet.</div>
              <div className="text-xs">
                Friend requests, messages, likes and badges will appear here.
              </div>
            </div>
          )}

          {items.map((row) => (
            <button
              key={row.notification_id}
              onClick={() => onRowClick(row)}
              data-testid={`notification-row-${row.notification_id}`}
              className={`flex w-full items-start gap-3 border-b border-zinc-100 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-900 ${
                row.read ? "opacity-70" : "bg-emerald-50/40 dark:bg-emerald-500/5"
              }`}
            >
              {/* Actor avatar (or emoji fallback for actor-less events like badges) */}
              {row.actor?.avatar_url ? (
                <img
                  src={row.actor.avatar_url}
                  alt=""
                  className="h-9 w-9 shrink-0 rounded-full object-cover"
                />
              ) : row.actor?.name ? (
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-emerald-500 text-sm font-semibold text-white">
                  {initial(row.actor.name)}
                </div>
              ) : (
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-zinc-100 text-lg dark:bg-zinc-800">
                  <span aria-hidden>{KIND_ICONS[row.kind] || KIND_ICONS.general}</span>
                </div>
              )}

              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium leading-snug text-zinc-900 dark:text-zinc-100">
                  {row.title}
                </div>
                {row.body && (
                  <div className="mt-0.5 line-clamp-2 text-xs text-zinc-500">{row.body}</div>
                )}
                <div className="mt-1 text-[11px] text-zinc-400">{timeAgo(row.created_at)}</div>
              </div>

              {!row.read && (
                <span
                  aria-label="unread"
                  className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500"
                  data-testid="notification-row-unread-dot"
                />
              )}
              {row.read && (
                <Check size={14} className="mt-1 shrink-0 text-zinc-400" aria-label="read" />
              )}
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
